import React, { useState, useEffect } from 'react';
import { CalendarClock, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { DateTimePicker } from './DateTimePicker';
import { bookingsApi } from '../../services/api';
import { formatDate, formatDateTime } from '../../lib/utils';
import { toast } from 'sonner';

// Bridge dialog: agents/admins revise a booking's ETA (date + time) with a mandatory reason.
export function EtaDialog({ booking, open, onOpenChange, onUpdated }) {
  const [newEta, setNewEta] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(function () {
    if (open && booking) { setNewEta(booking.estimated_delivery || ''); setReason(''); }
  }, [open, booking]);

  async function submit(e) {
    e.preventDefault();
    if (!newEta) { toast.error('Choose the revised arrival date'); return; }
    if (reason.trim().length < 3) { toast.error('Add a short reason for the change'); return; }
    setSaving(true);
    try {
      const res = await bookingsApi.updateEta(booking.id, { new_eta: newEta, reason: reason.trim() });
      toast.success('ETA revised to ' + formatDateTime(res.data.estimated_delivery));
      onOpenChange(false);
      if (onUpdated) onUpdated(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update the ETA');
    } finally {
      setSaving(false);
    }
  }

  if (!booking) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="eta-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-sm flex items-center justify-center"><CalendarClock className="h-5 w-5 text-amber-700" strokeWidth={1.75} /></div>
            Revise ETA
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="p-4 bg-gray-50 rounded-sm text-sm">
            <div className="flex items-center justify-between"><span className="sv-mono font-medium text-ink">{booking.booking_number}</span><span className="text-gray-600">{booking.origin_port.split(',')[0]} → {booking.destination_port.split(',')[0]}</span></div>
            <div className="text-gray-600 mt-1">Current ETA <span className="sv-mono text-ink" data-testid="eta-dialog-current">{formatDateTime(booking.estimated_delivery)}</span>{booking.original_eta && <span> · original <span className="sv-mono">{formatDate(booking.original_eta)}</span></span>}</div>
          </div>
          <div className="space-y-2">
            <Label>Revised arrival date and time</Label>
            <DateTimePicker value={newEta} onChange={setNewEta} minDate={booking.pickup_date} testId="eta-date-input" />
          </div>
          <div className="space-y-2">
            <Label>Reason (shared with the customer)</Label>
            <Textarea rows={2} value={reason} onChange={function (e) { setReason(e.target.value); }} placeholder="e.g. Port congestion at Singapore, vessel omitting Colombo call" className="rounded-sm" data-testid="eta-reason-input" />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" className="rounded-sm" onClick={function () { onOpenChange(false); }} data-testid="eta-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-sm bg-ink hover:bg-ink-800" data-testid="eta-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save revised ETA'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
